import { cn } from '@/lib/utils';

/**
 * Interruptor del sistema de diseño (para campos booleanos como `activo` de un método de
 * pago o `activo` de un canal).
 *
 * Es un `<button role="switch">` con `aria-checked`: así el teclado y los lectores de
 * pantalla lo tratan como lo que es, sin depender de un `<input type="checkbox">` estilizado.
 * El estado y el cambio los controla quien lo usa (componente controlado).
 */

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  /** Texto accesible cuando no hay un `label` visible asociado. */
  label?: string;
  disabled?: boolean;
  className?: string;
}

export function Switch({ checked, onChange, id, label, disabled = false, className }: SwitchProps) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'inline-flex h-6 w-11 shrink-0 items-center rounded-pill border transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
        'disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'border-brand-600 bg-brand-600' : 'border-line-strong bg-surface-muted',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'ml-0.5 size-5 rounded-pill bg-surface shadow-sm transition-transform',
          checked && 'translate-x-5',
        )}
      />
    </button>
  );
}
